import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Doughnut } from 'react-chartjs-2'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MpciApiClient from '../Common/services/MpciApiClient'
import BILL_STATUSES from '../Common/constants/billStatuses'
import { getChartColors } from '../Common/constants/colors'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'



const ALL_STATUSES_OPTION = 'All Statuses'

// Maps flat API field → { status code, label }
const STATUS_COUNTS_CONFIG = [
  { field: 'submCount',             status: 'SUBM',              label: 'Submitted to NAIC' },
  { field: 'submErrCount',          status: 'SUBM_ERR',          label: 'Error in Submission' },
  { field: 'contrlAckCount',        status: 'CONTRL_ACK',        label: 'Successful Acknowledgment' },
  { field: 'contrlNackCount',       status: 'CONTRL_NACK',       label: 'Failed Acknowledgement' },
  { field: 'actCount',              status: 'ACT',               label: 'Assessment Complete' },
  { field: 'acwCount',              status: 'ACW',               label: 'Assessment Complete with Warning' },
  { field: 'dnlCount',              status: 'DNL',               label: 'Do Not Load' },
  { field: 'expCount',              status: 'EXP',               label: 'Filing Expiry' },
  { field: 'psfCount',              status: 'PSF',               label: 'Pending Sub-filing' },
  { field: 'rfiCount',              status: 'RFI',               label: 'Request for Information' },
  { field: 'cancelContrlAckCount',  status: 'CANCEL_CONTRL_ACK', label: 'Cancellation Successful' },
  { field: 'cancelContrlNackCount', status: 'CANCEL_CONTRL_NACK',label: 'Cancellation Failed' }
]

const useStyles = makeStyles((theme) => ({
  legend: {
    '& .MuiTableCell-root': {
      padding: '6px 0',
      borderBottom: 0
    },
    '& .MuiTableBody-root .MuiTableRow-root': {
      backgroundColor: `${theme.palette.background.paper} !important`
    }
  },
  filterRow: {
    marginBottom: theme.spacing(2)
  },
  centerLabel: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    pointerEvents: 'none'
  },
  chartWrapper: {
    position: 'relative',
    width: '100%',
    maxWidth: 280,
    margin: '0 auto'
  }
}))

// orgIds/setOrgIds/canActAsOtherOrg/orgList are lifted to DashboardPage and shared with
// the KPI cards above — this is the ONE org filter for the whole dashboard, not a
// chart-local one.
function FilingsByStatusChart({ orgIds, setOrgIds, canActAsOtherOrg, orgList, isAdmin, defaultOrgIds, orgFilterLoading }) {
  const classes = useStyles()
  const theme = useTheme()
  const STATUS_COLORS = useMemo(() => getChartColors(theme), [theme])

  // statusCounts: [{ status, statusDescription, count }]
  const [statusCounts, setStatusCounts] = useState([])
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES_OPTION)
  const [loading, setLoading] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  function fetchStatusCounts() {
    if (!mountedRef.current) return
    setLoading(true)
    const params = new URLSearchParams()
    // BE expects LocalDateTime ISO format: 2026-05-01T00:00:00
    if (dateFrom) params.append('fromDate', `${dateFrom}T00:00:00`)
    if (dateTo) params.append('toDate', `${dateTo}T23:59:59`)
    if (statusFilter !== ALL_STATUSES_OPTION) params.append('status', statusFilter)
    // Nothing selected → default to own + child + grandchild orgs (non-admin) or omit
    // the filter entirely (admin sees all) — same client-computed default as the KPI
    // cards above (DashboardPage.js) and the other MPCI list pages. The BE's own
    // fallback for an omitted orgId only resolves to the caller's bare own org.
    const allowedOrgIds = orgList.map((o) => o.id)
    const picked = orgIds.length > 0 ? orgIds : null
    const clampedPicked = picked && allowedOrgIds.length > 0
      ? picked.filter((id) => allowedOrgIds.includes(id))
      : picked
    const resolvedOrgIds = isAdmin
      ? (clampedPicked && clampedPicked.length > 0 ? clampedPicked : null)
      : (clampedPicked && clampedPicked.length > 0 ? clampedPicked : defaultOrgIds)
    ;(resolvedOrgIds ?? []).forEach((id) => params.append('orgId', id))
    const url = `${UaeMpciApiUrls.DASHBOARD_STATUS_COUNTS}${params.toString() ? '?' + params.toString() : ''}`
    MpciApiClient.get(
      url,
      (data) => {
        if (!mountedRef.current) return
        // API returns flat object: { submCount, actCount, rfiCount, ... }
        const list = STATUS_COUNTS_CONFIG.map((cfg) => ({
          status:            cfg.status,
          statusDescription: cfg.label,
          count:             data?.[cfg.field] ?? 0
        }))
        setStatusCounts(list)
        setLoading(false)
      },
      (error) => {
        if (!mountedRef.current) return
        console.error('[Dashboard] Status counts fetch failed', error)
        setLoading(false)
      }
    )
  }

  // Skip while org scope is still resolving — see DashboardPage's KPI-fetch effect for why
  // this is keyed on orgFilterLoading rather than defaultOrgIds?.length.
  useEffect(() => {
    if (orgFilterLoading) return
    fetchStatusCounts()
  }, [dateFrom, dateTo, statusFilter, orgIds, isAdmin, orgFilterLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleClearFilters() {
    setDateFrom('')
    setDateTo('')
    setStatusFilter(ALL_STATUSES_OPTION)
    if (canActAsOtherOrg) setOrgIds([])
  }

  function handleRefresh() {
    fetchStatusCounts()
  }

    const filteredCounts = useMemo(() => {
    const active = statusFilter === ALL_STATUSES_OPTION
      ? statusCounts
      : statusCounts.filter((item) => item.status === statusFilter)
    // Only show statuses with count > 0 for cleaner chart
    return active.filter((item) => item.count > 0)
  }, [statusCounts, statusFilter])

  const totalCount = useMemo(
    () => filteredCounts.reduce((sum, item) => sum + item.count, 0),
    [filteredCounts]
  )

  const chartData = useMemo(
    () => ({
      labels: filteredCounts.map((item) => item.statusDescription),
      datasets: [
        {
          data: filteredCounts.map((item) => item.count),
                    backgroundColor: filteredCounts.map(
            (item) => STATUS_COLORS[item.status] || theme.palette.divider
          ),
          borderWidth: 1
        }
      ]
    }),
    [filteredCounts, STATUS_COLORS, theme.palette.divider]
  )

  const chartOptions = {
    cutoutPercentage: 60,
    legend: { display: false },
    tooltips: {
      callbacks: {
        label: (tooltipItem, chartData) => {
          const label = chartData.labels[tooltipItem.index] || ''
          const value = chartData.datasets[0].data[tooltipItem.index] || 0
          const pct = totalCount > 0 ? ((value / totalCount) * 100).toFixed(1) : 0
          return ` ${label}: ${value} (${pct}%)`
        }
      }
    }
  }

  return (
    <Card>
      <CardContent>
        <Typography variant='h6' style={{ fontWeight: 600, marginBottom: 16 }}>
          Filings by Status
        </Typography>
                <Grid container spacing={2} className={classes.filterRow}>
          <Grid item xs={12} sm={3}>
            <TextField
              label='Date From'
              type='date'
              size='small'
              variant='outlined'
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label='Date To'
              type='date'
              size='small'
              variant='outlined'
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl variant='outlined' size='small' fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label='Status'
              >
                <MenuItem value={ALL_STATUSES_OPTION}>{ALL_STATUSES_OPTION}</MenuItem>
                {STATUS_COUNTS_CONFIG.map((cfg) => (
                  <MenuItem key={cfg.status} value={cfg.status}>{cfg.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

                    {/* Organisation multiselect — hidden entirely for a non-admin/
                non-parent-corp org, shown for admin/parent-corp. This is the ONE org
                filter for the whole dashboard — it also drives the KPI cards above. */}
          {canActAsOtherOrg && (
            <Grid item xs={12} sm={3}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={orgList}
                getOptionLabel={(opt) => opt.name ?? ''}
                getOptionSelected={(opt, val) => opt.id === val.id}
                value={orgList.filter((o) => orgIds.includes(o.id))}
                onChange={(_, newVal) => setOrgIds(newVal.map((o) => o.id))}
                renderOption={(option, { selected }) => (
                  <>
                    <Checkbox size='small' checked={selected} style={{ padding: 4, marginRight: 4 }} />
                    <ListItemText primary={option.name} />
                  </>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Organisation'
                    variant='outlined'
                    size='small'
                    fullWidth
                    placeholder={orgIds.length > 0 ? '' : 'All'}
                  />
                )}
                noOptionsText='No organisations found'
              />
            </Grid>
          )}

          <Grid item xs={12} sm='auto'>
            <Box display='flex' alignItems='center' style={{ gap: 8 }}>
              <Button
                variant='outlined'
                size='small'
                startIcon={<FontAwesomeIcon icon={['fal', 'xmark']} />}
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
              <Tooltip title='Refresh'>
                <IconButton size='small' onClick={handleRefresh} disabled={loading}>
                  <FontAwesomeIcon icon={['fal', 'refresh']} spin={loading} />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
                {loading ? (
          <Box display='flex' justifyContent='center' alignItems='center' minHeight={200}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Box className={classes.chartWrapper}>
                <Doughnut data={chartData} options={chartOptions} />
                <Box className={classes.centerLabel}>
                  <Typography variant='h4' style={{ fontWeight: 700 }}>
                    {totalCount}
                  </Typography>
                  <Typography variant='caption' color='textSecondary'>
                    Total Filings
                  </Typography>
                </Box>
              </Box>
            </Grid>
                        <Grid item xs={12} md={7}>
              <Table className={classes.legend} size='small'>
                <TableBody>
                  {filteredCounts.map((item) => {
                    const pct = totalCount > 0 ? ((item.count / totalCount) * 100).toFixed(1) : 0
                    const color = STATUS_COLORS[item.status] || theme.palette.divider
                    return (
                      <TableRow key={item.status}>
                        <TableCell>
                          <Grid container alignItems='center' spacing={1}>
                            <Grid item>
                              <Box
                                bgcolor={color}
                                borderRadius='50%'
                                height={12}
                                width={12}
                              />
                            </Grid>
                            <Grid item xs>
                              <Typography variant='body2'>{item.statusDescription}</Typography>
                            </Grid>
                          </Grid>
                        </TableCell>
                        <TableCell align='right'>
                          <Typography variant='body2' style={{ fontWeight: 600 }}>
                            {item.count}
                          </Typography>
                        </TableCell>
                        <TableCell align='right'>
                          <Typography variant='caption' color='textSecondary'>
                            {pct}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  )
}

export default FilingsByStatusChart